/** Position ownership follows the opening action, never a signal or call/put label. */
export const getPositionSideFromOpenType = (openType) => {
    switch (openType) {
        case 'buy':
        case 'buyToOpen':
            return 'long';
        case 'shortSell':
        case 'sellToOpen':
            return 'short';
        default:
            throw new Error(`Unsupported opening transaction type: ${openType}`);
    }
};
/**
 * Exposure of one leg, not a strategy forecast or aggregate portfolio exposure.
 * Cash instruments require null; option openings require an explicit call/put.
 */
export const getUnderlyingBiasFromOpenType = (openType, optionType) => {
    const side = getPositionSideFromOpenType(openType);
    const isOptionOpening = openType === 'buyToOpen' || openType === 'sellToOpen';
    if (isOptionOpening ? optionType !== 'call' && optionType !== 'put' : optionType !== null) {
        throw new Error(`Invalid option type ${optionType} for opening transaction type ${openType}`);
    }
    const isBullish = optionType === 'put' ? side === 'short' : side === 'long';
    return isBullish ? 'bullish' : 'bearish';
};
/** Derive the public leg view without inventing a type for unresolved saved option legs. */
export const getStrategyLegSemantics = (leg) => {
    const positionSide = getPositionSideFromOpenType(leg.openTransactionType);
    if (leg.derivativeType === 'Option') {
        if (leg.openTransactionType !== 'buyToOpen' && leg.openTransactionType !== 'sellToOpen') {
            throw new Error('Option legs require buyToOpen or sellToOpen');
        }
        return {
            positionSide,
            underlyingBias: leg.optionType === null ? null : getUnderlyingBiasFromOpenType(leg.openTransactionType, leg.optionType)
        };
    }
    if (leg.optionType !== null)
        throw new Error('Only option legs may specify optionType');
    if (leg.derivativeType === 'Future') {
        if (leg.openTransactionType !== 'buyToOpen' && leg.openTransactionType !== 'sellToOpen') {
            throw new Error('Future legs require buyToOpen or sellToOpen');
        }
        return { positionSide, underlyingBias: null };
    }
    if (leg.derivativeType !== null)
        throw new Error(`Unsupported derivative type: ${leg.derivativeType}`);
    if (leg.openTransactionType !== 'buy' && leg.openTransactionType !== 'shortSell') {
        throw new Error('Cash legs require buy or shortSell');
    }
    return { positionSide, underlyingBias: getUnderlyingBiasFromOpenType(leg.openTransactionType, null) };
};
/** Summarize leg exposure for review; mixed legs do not imply a net directional exposure. */
export const summarizeLegUnderlyingBias = (legs) => {
    if (legs.length === 0 || legs.some((leg) => leg.underlyingBias === null))
        return 'unknown';
    const biases = new Set(legs.map((leg) => leg.underlyingBias));
    if (biases.size > 1)
        return 'mixed';
    return biases.has('bullish') ? 'bullish' : 'bearish';
};
/** Summarize the ownership side required by scalar entry and backtest headers. */
export const summarizeLegPositionSide = (legs) => {
    if (legs.length === 0)
        return 'unknown';
    const sides = new Set(legs.map((leg) => leg.positionSide));
    if (sides.size > 1)
        return 'mixed';
    return sides.has('long') ? 'long' : 'short';
};
//# sourceMappingURL=tradeSemantics.js.map